import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ErrorMessage from "../../components/StudioErstellen/ErrorMessage";
import classes from "./CreativeProjectEdit.module.css";
import { ChangeStudioModal } from "./ChangeStudioModal";
import { AddingVideoFotoModal } from "./AddingVideoFotoModal";
import { SmallModal } from "../../components/UI/SmallModal/SmallModal";
import AvatarEditor from 'react-avatar-editor';
import { useCreateCreativeProject } from "../../hooks/creative/useCreateCreativeProject";
import { useUpdateCreativeProject } from "../../hooks/creative/useUpdateCreativeProject";
import { useCreativeProject } from "../../hooks/creative/useCreativeProjects";
import { useDeleteCreativeProject } from "../../hooks/creative/useDeleteCreativeProject";
import {
  PROJECT_AUDIO_ACCEPT,
  PROJECT_AUDIO_COVER_ACCEPT,
  PROJECT_IMAGE_ACCEPT,
  PROJECT_VIDEO_ACCEPT,
  isAllowedProjectAudioCoverFile,
  isAllowedProjectAudioFile,
  isAllowedProjectImageFile,
  isAllowedProjectVideoFile,
  saveCreativeProjectWithOwnedMedia,
} from "./creativeProjectMediaFlow";
import { getCloudFrontImageUrl } from "../../util/cloudFrontUtils";
import awsConfig from "../../config/aws-config";
import { useFetchStudioById } from "../../hooks/useFetchStudiosForService";
import { buildCoarseLocationText } from "../../util/locationPrivacy";
import { notifyDeleteError, notifySaveError, notifyValidationToast } from "../../util/notifyActionError";

const genreTags = [
  "Pop", "Rock", "Hip-Hop", "Jazz", "Blues", "Country", "Electronic", "Folk", "Funk", "Gospel", "Heavy Metal", "Indie", "Latin", "Metal", "Punk", "R&B", "Reggae", "Rock", "Soul", "Techno", "Trance", "Trip-Hop", "World Music"
];

function isAllowedAudioUpload(file) {
  return isAllowedProjectAudioFile(file);
}

export const CreativeProjectEdit = ({ creative, loading, error }) => {
  const navigate = useNavigate();
  const params = useParams();
  const projectId = params.projectId;  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [showChangeStudioModal, setShowChangeStudioModal] = useState(false);
  const [showAddMediaModal, setShowAddMediaModal] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isStudioCheckboxChecked, setIsStudioCheckboxChecked] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const uploadIntervalsRef = useRef({}); // Store intervals by index
  const cancelledUploadsRef = useRef(new Set()); // Track cancelled uploads
  const audioUploadIntervalsRef = useRef({}); // Store intervals for audio uploads
  const cancelledAudioUploadsRef = useRef(new Set()); // Track cancelled audio uploads
  const [isGenreDropdownOpen, setIsGenreDropdownOpen] = useState(false);
  const genreDropdownRef = useRef(null);
  const genreButtonRef = useRef(null);
  
  // Cover image editor state
  const coverImageEditorRef = useRef(null);
  const coverImageEditorContainerRef = useRef(null);
  const coverImageFileInputRef = useRef(null);
  const [coverImageModalOpen, setCoverImageModalOpen] = useState(false);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [coverImageScale, setCoverImageScale] = useState(1);
  const [coverImagePosition, setCoverImagePosition] = useState({ x: 0.5, y: 0.5 });
  const [coverImageFormData, setCoverImageFormData] = useState({ coverImage: null });
  const [editingCoverImageIndex, setEditingCoverImageIndex] = useState(null);
  const { handleCreateProject, loading: createLoading } = useCreateCreativeProject();
  const { handleUpdateProject, loading: updateLoading } = useUpdateCreativeProject();
  const { handleDeleteProject, loading: deleteLoading } = useDeleteCreativeProject();
  
  // Fetch project if projectId exists in URL (editing mode)
  const { project, loading: projectLoading, error: projectError } = useCreativeProject(projectId);

  const isEditing = !!projectId && !!project;
  const isMusicType = creative?.creativeType === 'MUSIC';
  const isVisualType = creative?.creativeType === 'VISUAL';
  
  // Form state
  const [formValues, setFormValues] = useState({
    title: '',
    description: '',
    studioID: null,
    status: '',
    images: [], // For VISUAL type
    audios: [],  // For MUSIC type - each audio can have: url, title, artists, genre, order, durationMs, coverImage, isUploading, uploadProgress, file
    artists: [],  // Array of artist names for MUSIC type
    genre: null // Selected genre for MUSIC type
  });

  const [currentArtistInput, setCurrentArtistInput] = useState('');
  
  const [errors, setErrors] = useState({
    title: '',
    description: '',
    studioID: '',
    images: '',
    audios: '',
    coverImages: ''
  });

  const [status, setStatus] = useState('ACTIVE');

  const handleStatusChange = (e) => {
    const newStatus = e.target.checked ? 'ACTIVE' : 'INACTIVE';
    setStatus(newStatus);
    setFormValues(prev => ({ ...prev, status: newStatus }));
  };

  
  // Initialize form values from project if editing
  useEffect(() => {
    if (project) {
      // Map existing images to include order
      const mappedImages = (project.images || []).map((img, idx) => ({
        ...(typeof img === 'string' ? { url: img } : img),
        order: idx
      }));
      
      // Process audios - preserve all fields including cover images
      const mappedAudios = (project.audios || []).map((audio, idx) => {
        const audioObj = {
          ...audio,
          order: audio.order !== undefined ? audio.order : idx
        };
        // Preserve cover image as string (from database)
        // The processedCoverImage is for display only, we keep the original coverImage key
        if (audio.coverImage) {
          audioObj.coverImage = audio.coverImage; // Keep as string key
        }
        // Preserve audioKey if available (for URL resolution)
        if (audio.audioKey) {
          audioObj.audioKey = audio.audioKey;
        }
        return audioObj;
      });
      
      // Extract artists and genre from first audio (for project-level display)
      // Note: In the schema, artists and genre are stored on individual audio items
      // But for the form, we display them at project level and apply to all audios on save
      let artistsArray = [];
      let projectGenre = null;
      
      if (mappedAudios.length > 0) {
        const firstAudio = mappedAudios[0];
        // Parse artists from first audio
        if (firstAudio.artists) {
          if (Array.isArray(firstAudio.artists)) {
            artistsArray = firstAudio.artists;
          } else if (typeof firstAudio.artists === 'string') {
            // If it's a string, try to split by comma
            artistsArray = firstAudio.artists.split(',').map(a => a.trim()).filter(a => a);
          }
        }
        // Get genre from first audio
        projectGenre = firstAudio.genre || null;
      }
      
      // Also check project-level artists/genre (for backward compatibility)
      if (artistsArray.length === 0 && project.artists) {
        if (Array.isArray(project.artists)) {
          artistsArray = project.artists;
        } else if (typeof project.artists === 'string') {
          artistsArray = project.artists.split(',').map(a => a.trim()).filter(a => a);
        }
      }
      if (!projectGenre && project.genre) {
        projectGenre = project.genre;
      }
      
      // Map project status to display status for toggle:
      // PUBLISHED → ACTIVE (toggle on)
      // DRAFT or INACTIVE → INACTIVE (toggle off)
      const projectStatus = project.status || 'DRAFT';
      const displayStatus = projectStatus === 'PUBLISHED' ? 'ACTIVE' : 'INACTIVE';
      
      setStatus(displayStatus);
      setFormValues({
        title: project.title || '',
        description: project.description || '',
        studioID: project.studioID || null,
        status: displayStatus,
        images: mappedImages,
        audios: mappedAudios,
        artists: artistsArray,
        genre: projectGenre
      });
      // Set checkbox state based on whether project has a studio
      setIsStudioCheckboxChecked(!!project.studioID);
    } else if (creative) {
      // For new projects, start with no studio selected and checkbox unchecked
      // Default status is ACTIVE
      setStatus('ACTIVE');
      setIsStudioCheckboxChecked(false);
      setFormValues(prev => ({
        ...prev,
        studioID: null,
        status: 'ACTIVE'
      }));
    }
  }, [project, creative]);
  
  // Check if form is valid (all required fields filled)
  const isFormValid = () => {
    if (!formValues.title?.trim()) return false;
    if (!formValues.description?.trim() || formValues.description.trim().length < 10) return false;
    if (isVisualType && (!formValues.images || formValues.images.length === 0)) return false;
    if (isMusicType && (!formValues.audios || formValues.audios.length === 0)) return false;
    
    // Validate cover images for MUSIC type - all audios must have cover images
    if (isMusicType && formValues.audios && formValues.audios.length > 0) {
      const audiosWithoutCoverImages = formValues.audios.filter(audio => {
        // Check if audio has a cover image (either as file, string, or processed)
        const hasCoverImage = 
          (audio.coverImage && (typeof audio.coverImage === 'string' || audio.coverImage.file || audio.coverImage.url)) ||
          audio.processedCoverImage;
        return !hasCoverImage;
      });
      
      if (audiosWithoutCoverImages.length > 0) {
        return false;
      }
    }
    
    return true;
  };
  
  // Check if there are changes (only relevant for editing)
  const hasChanges = () => {
    if (!project) return false; // No changes if no project to compare
    
    // Compare title
    if (formValues.title !== (project.title || '')) return true;
    
    // Compare description
    if (formValues.description !== (project.description || '')) return true;
    
    // Compare studioID
    if (formValues.studioID !== (project.studioID || null)) return true;
    
    // Compare status - convert project status to display format for comparison
    // PUBLISHED → ACTIVE (toggle on), DRAFT/INACTIVE → INACTIVE (toggle off)
    const projectStatus = project.status || 'DRAFT';
    const originalDisplayStatus = projectStatus === 'PUBLISHED' ? 'ACTIVE' : 'INACTIVE';
    if (status !== originalDisplayStatus) return true;
    
    // Compare images (for VISUAL type)
    if (isVisualType) {
      const currentImages = formValues.images || [];
      const originalImages = project.images || [];
      if (currentImages.length !== originalImages.length) return true;
      
      // Compare each image URL and type
      for (let i = 0; i < currentImages.length; i++) {
        const current = currentImages[i];
        const original = originalImages[i];
        
        // Normalize to objects for comparison
        const currentObj = typeof current === 'string' ? { url: current } : current;
        const originalObj = typeof original === 'string' ? { url: original } : original;
        
        // Compare URL
        if (currentObj?.url !== originalObj?.url) return true;
        
        // Compare type (for links)
        if (currentObj?.type !== originalObj?.type) return true;
      }
    }
    
    // Compare audios (for MUSIC type)
    if (isMusicType) {
      const currentAudios = formValues.audios || [];
      const originalAudios = project.audios || [];
      if (currentAudios.length !== originalAudios.length) return true;
      
      // Compare each audio field
      for (let i = 0; i < currentAudios.length; i++) {
        const current = currentAudios[i];
        const original = originalAudios[i];
        
        // Compare URL (handle audioKey/originalUrl fallbacks)
        const currentUrl = current?.url || current?.audioKey || current?.originalUrl;
        const originalUrl = original?.url || original?.audioKey || original?.originalUrl;
        if (currentUrl !== originalUrl) return true;
        
        // Compare title
        if (current?.title !== original?.title) return true;
        
        // Compare cover image (handle both string and object)
        const currentCover = typeof current?.coverImage === 'string' ? current.coverImage : current?.coverImage?.url || null;
        const originalCover = typeof original?.coverImage === 'string' ? original.coverImage : original?.coverImage?.url || null;
        if (currentCover !== originalCover) return true;
        
        // Compare artists
        const currentArtists = current?.artists || '';
        const originalArtists = original?.artists || '';
        if (currentArtists !== originalArtists) return true;
        
        // Compare genre
        if (current?.genre !== original?.genre) return true;
      }
      
      // Compare project-level artists and genre (extracted from first audio)
      // Get original artists/genre from first audio
      const originalFirstAudio = originalAudios[0];
      let originalArtistsArray = [];
      let originalGenre = null;
      if (originalFirstAudio?.artists) {
        if (Array.isArray(originalFirstAudio.artists)) {
          originalArtistsArray = originalFirstAudio.artists;
        } else if (typeof originalFirstAudio.artists === 'string') {
          originalArtistsArray = originalFirstAudio.artists.split(',').map(a => a.trim()).filter(a => a);
        }
      }
      originalGenre = originalFirstAudio?.genre || null;
      
      // Compare with current form values
      const currentArtistsStr = (formValues.artists || []).join(', ');
      const originalArtistsStr = originalArtistsArray.join(', ');
      if (currentArtistsStr !== originalArtistsStr) return true;
      
      if (formValues.genre !== originalGenre) return true;
    }
    
    return false;
  };
  
  // Check if any files are currently uploading
  const hasUploadsInProgress = () => {
    // Check images for VISUAL type
    if (isVisualType && formValues.images) {
      const hasUploadingImages = formValues.images.some(img => img.isUploading === true);
      if (hasUploadingImages) return true;
    }
    
    // Check audios for MUSIC type
    if (isMusicType && formValues.audios) {
      const hasUploadingAudios = formValues.audios.some(audio => audio.isUploading === true);
      if (hasUploadingAudios) return true;
    }
    
    return false;
  };
  
  // Determine if save button should be enabled
  const canSave = () => {
    // Don't allow saving if files are still uploading
    if (hasUploadsInProgress()) {
      return false;
    }
    
    if (isEditing) {
      // For editing: form must be valid AND have changes
      return isFormValid() && hasChanges();
    } else {
      // For new project: form must be valid
      return isFormValid();
    }
  };
  
  const handleTitleChange = (e) => {
    const value = e.target.value;
    
    // Limit to 100 characters
    if (value.length > 100) {
      return;
    }
    
    setFormValues(prev => ({ ...prev, title: value }));
    
    // Clear validation error when user types
    if (errors.title) {
      setErrors(prev => ({ ...prev, title: '' }));
    }
  };
  
  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    
    // Limit to 500 characters
    if (value.length > 500) {
      return;
    }
    
    setFormValues(prev => ({ ...prev, description: value }));
    
    // Validate and set error
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, description: 'Du musst eine Beschreibung für dein Projekt eingeben.' }));
    } else if (value.trim().length < 10) {
      setErrors(prev => ({ ...prev, description: 'Du musst mindestens 10 Zeichen für deine Beschreibung eingeben.' }));
    } else {
      // Clear validation error when valid
      setErrors(prev => {
        if (!prev.description) return prev;
        return { ...prev, description: '' };
      });
    }
  };
  
  const handleStudioSelect = (studioId) => {
    setFormValues(prev => ({ ...prev, studioID: studioId }));
    setIsStudioCheckboxChecked(true);
    setShowChangeStudioModal(false);
    
    // Clear validation error
    if (errors.studioID) {
      setErrors(prev => ({ ...prev, studioID: '' }));
    }
  };

  // Handle checkbox click - toggle studio selection
  const handleStudioCheckboxClick = (e) => {
    e.stopPropagation();
    if (isStudioCheckboxChecked) {
      // If checkbox is checked, uncheck it and clear studio
      setIsStudioCheckboxChecked(false);
      setFormValues(prev => ({ ...prev, studioID: null }));
      if (errors.studioID) {
        setErrors(prev => ({ ...prev, studioID: '' }));
      }
    } else { 
      // If checkbox is unchecked, check it and open modal
      setIsStudioCheckboxChecked(true);
      setShowChangeStudioModal(true);
    }
  };
  
  // Calculate total size of all media items
  const calculateTotalSize = (mediaItems) => {
    return mediaItems.reduce((total, item) => {
      if (item.type === 'link') return total; // Links don't count
      if (item.file) return total + (item.file.size || 0);
      if (item.size) return total + item.size;
      // Existing items from database might not have size - they're already uploaded
      // so we don't count them towards the limit
      return total;
    }, 0);
  };

  // Handle mode selection from modal (photo or video)
  const handleModeSelect = (mode) => {
    setShowAddMediaModal(false);
    
    // Trigger appropriate file input after modal closes
    setTimeout(() => {
      if (mode === 'photo' && photoInputRef.current) {
        photoInputRef.current.click();
      } else if (mode === 'video' && videoInputRef.current) {
        videoInputRef.current.click();
      }
    }, 100);
  };

  // Handle link submission from modal
  const handleLinkSubmit = (linkItem) => {
    // Add link item to state first (similar to file uploads)
    const startIndex = formValues.images?.length || 0;
    
    setFormValues(prev => ({
      ...prev,
      images: [...(prev.images || []), {
        ...linkItem,
        order: startIndex,
        isUploading: false,
        uploadProgress: 100
      }]
    }));
    
    // Process the link item
    setTimeout(() => {
      handleAddMedia([linkItem], startIndex);
    }, 0);
  };

  // Generate thumbnail from video file
  const generateVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      
      video.onloadedmetadata = () => {
        // Seek to 1 second or 10% of video, whichever is smaller
        const seekTime = Math.min(1, video.duration * 0.1);
        video.currentTime = seekTime;
      };
      
      video.onseeked = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const thumbnailUrl = URL.createObjectURL(blob);
            resolve(thumbnailUrl);
          } else {
            resolve(null);
          }
        }, 'image/jpeg', 0.8);
      };
      
      video.onerror = () => {
        resolve(null);
      };
      
      video.src = URL.createObjectURL(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (e, type) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) {
      return;
    }

    const isValidFile = type === 'video'
      ? isAllowedProjectVideoFile
      : isAllowedProjectImageFile;
    const invalidFile = files.find((file) => !isValidFile(file));

    if (invalidFile) {
      notifyValidationToast(
        type === 'video'
          ? 'Bitte nur MP4-, MOV- oder WEBM-Videos hochladen.'
          : 'Bitte nur JPG-, PNG-, WEBP-, HEIC- oder HEIF-Bilder hochladen.',
        'Ungültiges Format'
      );
      if (e.target) {
        e.target.value = '';
      }
      return;
    }

    // Generate thumbnails for videos
    const mediaItems = await Promise.all(files.map(async (file) => {
      let previewUrl = URL.createObjectURL(file);
      let thumbnailUrl = null;
      
      if (type === 'video') {
        // Generate thumbnail for video
        thumbnailUrl = await generateVideoThumbnail(file);
        if (thumbnailUrl) {
          previewUrl = thumbnailUrl; // Use thumbnail as preview
        }
      }
      
      return {
        type: type,
        file: file,
        previewUrl: previewUrl,
        thumbnailUrl: thumbnailUrl,
        size: file.size,
        uploadProgress: 100,
        isUploading: false
      };
    }));

    
    // Calculate start index based on current state before adding items
    const startIndex = formValues.images?.length || 0;
    
    // Add items immediately to show them with upload progress
    setFormValues(prev => ({
      ...prev,
      images: [...(prev.images || []), ...mediaItems]
    }));
    
    setTimeout(() => {
      handleAddMedia(mediaItems, startIndex);
    }, 0);
    
    // Reset file input
    if (e.target) {
      e.target.value = '';
    }
  };

  // Update upload progress for a specific item
  const updateUploadProgress = (itemIndex, progress) => {
    setFormValues(prev => {
      const newImages = [...(prev.images || [])];
      if (newImages[itemIndex]) {
        newImages[itemIndex] = {
          ...newImages[itemIndex],
          uploadProgress: progress
        };
      }
      return {
        ...prev,
        images: newImages
      };
    });
  };

  // Handle media items from modal (photos, videos, links)
  const handleAddMedia = async (newMediaItems, providedStartIndex = null) => {
    if (!isVisualType) {
      return;
    }

    let startIndex = providedStartIndex;
    if (startIndex === null) {
      startIndex = formValues.images.length;
    }

    const newSize = newMediaItems.reduce((total, item) => {
      if (item.type === 'link') return total; // Links don't count
      if (item.file) return total + (item.file.size || 0);
      if (item.size) return total + item.size;
      return total;
    }, 0);
    const newFiles = new Set(newMediaItems.map((item) => item.file).filter(Boolean));
    
    // Calculate size of existing items that have file info (newly added but not yet saved)
    const existingNewItemsSize = (formValues.images || []).reduce((total, item) => {
      if (item.type === 'link') return total;
      if (item.file && !newFiles.has(item.file) && !item.isUploading) return total + (item.file.size || 0);
      if (item.size && !item.isUploading) return total + item.size;
      return total; // Existing uploaded items don't have file/size, so don't count
    }, 0);

    const totalSize = existingNewItemsSize + newSize;
    const maxSize = 100 * 1024 * 1024;

    if (totalSize > maxSize) {
      setFormValues(prev => ({
        ...prev,
        images: prev.images.slice(0, startIndex)
      }));
      setErrors(prev => ({ 
        ...prev, 
        images: `Die Gesamtgröße darf 100MB nicht überschreiten. Aktuell: ${(totalSize / (1024 * 1024)).toFixed(2)}MB`
      }));
      return;
    }

    const processedItems = newMediaItems.map((item, idx) => {
      const currentIndex = startIndex + idx;
      updateUploadProgress(currentIndex, 100);

      if (item.type === 'link') {
        return {
          type: 'link',
          url: item.url,
          thumbnailUrl: item.thumbnailUrl,
          order: currentIndex,
          isUploading: false,
          uploadProgress: 100
        };
      }

      return {
        ...item,
        url: item.url || item.previewUrl || '',
        caption: item.caption || '',
        order: currentIndex,
        isUploading: false,
        uploadProgress: 100
      };
    });

    setFormValues(prev => {
      const newImages = [...(prev.images || [])];

      processedItems.forEach((processedItem, idx) => {
        const originalItem = newMediaItems[idx];
        let itemIndex = -1;

        if (originalItem?.type === 'link') {
          itemIndex = newImages.findIndex((img) =>
            img.type === 'link' && img.url === originalItem.url
          );
        } else if (originalItem?.file) {
          itemIndex = newImages.findIndex((img) => img.file === originalItem.file);
        }

        if (itemIndex < 0) {
          itemIndex = startIndex + idx;
        }

        newImages[itemIndex] = {
          ...(newImages[itemIndex] || {}),
          ...processedItem
        };
      });

      return {
        ...prev,
        images: newImages
      };
    });

    if (errors.images) {
      setErrors(prev => ({ ...prev, images: '' }));
    }
  };

  // Legacy function for backward compatibility
  const handleImageUpload = async (files) => {
    if (!isVisualType) return;

    const selectedFiles = Array.from(files);
    const invalidFile = selectedFiles.find((file) => (
      isAllowedProjectVideoFile(file)
        ? false
        : !isAllowedProjectImageFile(file)
    ));

    if (invalidFile) {
      setErrors(prev => ({
        ...prev,
        images: 'Bitte nur JPG-, PNG-, WEBP-, HEIC-, HEIF-Bilder oder MP4-, MOV-, WEBM-Videos hochladen.'
      }));
      return;
    }

    const mediaItems = selectedFiles.map(file => ({
      type: isAllowedProjectImageFile(file) ? 'photo' : 'video',
      file: file,
      previewUrl: URL.createObjectURL(file),
      size: file.size
    }));
    
    const startIndex = formValues.images?.length || 0;
    setFormValues(prev => ({
      ...prev,
      images: [...(prev.images || []), ...mediaItems]
    }));
    await handleAddMedia(mediaItems, startIndex);
  };

  // Handle current artist input change
  const handleArtistInputChange = (e) => {
    setCurrentArtistInput(e.target.value);
  };

  // Handle adding artist on Enter key
  const handleArtistKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addArtist();
    }
  };

  // Add artist to the list
  const addArtist = () => {
    const artistName = currentArtistInput.trim();
    
    if (!artistName) {
      return; // Don't add empty artists
    }
    
    // Check if artist already exists (case-insensitive)
    const artistExists = formValues.artists.some(
      artist => artist.toLowerCase() === artistName.toLowerCase()
    );
    
    if (artistExists) {
      // Clear input but don't add duplicate
      setCurrentArtistInput('');
      return;
    }
    
    // Add artist to the array
    setFormValues(prev => ({
      ...prev,
      artists: [...(prev.artists || []), artistName]
    }));
    
    // Clear input
    setCurrentArtistInput('');
    
    // Clear error if exists
    if (errors.artist) {
      setErrors(prev => ({ ...prev, artist: '' }));
    }
  };

  // Handle remove individual artist
  const handleRemoveArtist = (indexToRemove) => {
    setFormValues(prev => ({
      ...prev,
      artists: prev.artists.filter((_, index) => index !== indexToRemove)
    }));
  };

  // Handle genre selection
  const handleGenreSelect = (genre) => {
    setFormValues(prev => ({
      ...prev,
      genre: genre
    }));
    setIsGenreDropdownOpen(false);
    
    // Clear error if exists
    if (errors.genre) {
      setErrors(prev => ({ ...prev, genre: '' }));
    }
  };

  // Handle genre dropdown toggle
  const toggleGenreDropdown = () => {
    setIsGenreDropdownOpen(prev => !prev);
  };

  // Close genre dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        genreDropdownRef.current &&
        !genreDropdownRef.current.contains(event.target) &&
        genreButtonRef.current &&
        !genreButtonRef.current.contains(event.target)
      ) {
        setIsGenreDropdownOpen(false);
      }
    };

    if (isGenreDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isGenreDropdownOpen]);

  // Calculate dropdown position (above or below)
  useEffect(() => {
    if (isGenreDropdownOpen && genreButtonRef.current && genreDropdownRef.current) {
      const buttonRect = genreButtonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const dropdownHeight = 400; // Max height we want
      
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        // Position above
        genreDropdownRef.current.style.bottom = '100%';
        genreDropdownRef.current.style.top = 'auto';
        genreDropdownRef.current.style.marginBottom = '8px';
        genreDropdownRef.current.style.marginTop = '0';
      } else {
        // Position below (default)
        genreDropdownRef.current.style.top = '100%';
        genreDropdownRef.current.style.bottom = 'auto';
        genreDropdownRef.current.style.marginTop = '8px';
        genreDropdownRef.current.style.marginBottom = '0';
      }
    }
  }, [isGenreDropdownOpen]);
  
  // Get audio duration in milliseconds from file
  const getAudioDuration = (file) => {
    return new Promise((resolve) => {
      const audio = document.createElement('audio');
      const url = URL.createObjectURL(file);
      
      audio.preload = 'metadata';
      audio.src = url;
      
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        // Convert seconds to milliseconds
        const durationMs = Math.round(audio.duration * 1000);
        resolve(durationMs || 0);
      };
      
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(0); // Return 0 if we can't get duration
      };
    });
  };

  // Handle audio uploads for MUSIC type
  const handleAudioUpload = async (e) => {
    if (!isMusicType) return;
    
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0]; // Only handle one file at a time
    const audioInput = e.target;
    const replaceIndex = audioInput?.dataset?.replaceIndex;

    if (!isAllowedAudioUpload(file)) {
      notifyValidationToast('Bitte nur MP3-, M4A-, AAC-, WAV-, WEBM- oder OGG-Audiodateien hochladen.', 'Ungültiges Format');
      if (audioInput) {
        audioInput.value = '';
      }
      return;
    }
    
    // If replacing, remove the old audio first
    if (replaceIndex !== undefined) {
      const indexToReplace = parseInt(replaceIndex);
      const oldAudio = formValues.audios[indexToReplace];
      
      // Preserve cover image if exists
      const preservedCoverImage = oldAudio?.coverImage;
      
      // Remove old audio
      setFormValues(prev => {
        const newAudios = prev.audios.filter((_, i) => i !== indexToReplace);
        return {
          ...prev,
          audios: newAudios
        };
      });
      
      // Wait a bit for state to update, then add new audio
      setTimeout(() => {
        setFormValues(prev => {
          const newAudioItem = {
            file: file,
            title: file.name.replace(/\.[^/.]+$/, ''),
            isUploading: true,
            uploadProgress: 0,
            order: indexToReplace,
            coverImage: preservedCoverImage || null
          };
          
          // Start upload
          setTimeout(() => {
            processAudioUploads([newAudioItem], indexToReplace);
          }, 0);
          
          return {
            ...prev,
            audios: [...prev.audios, newAudioItem]
          };
        });
      }, 100);
      
      delete audioInput.dataset.replaceIndex;
    } else {
      const startIndex = formValues.audios.length;
      const newAudioItem = {
        file: file,
        title: file.name.replace(/\.[^/.]+$/, ''),
        isUploading: true,
        uploadProgress: 0,
        order: startIndex,
        coverImage: null
      };
      
      setFormValues(prev => ({
        ...prev,
        audios: [...prev.audios, newAudioItem]
      }));
      
      // Start upload
      setTimeout(() => {
        processAudioUploads([newAudioItem], startIndex);
      }, 0);
    }
    
    // Reset input
    if (audioInput) {
      audioInput.value = '';
    }
    
    // Clear validation error
    if (errors.audios) {
      setErrors(prev => ({ ...prev, audios: '' }));
    }
  };
  
  // Process audio files locally; ownedMedia upload happens on save.
  const processAudioUploads = async (audioItems, startIndex) => {
    const processedAudios = await Promise.all(audioItems.map(async (audioItem, idx) => {
      const currentIndex = startIndex + idx;
      const file = audioItem.file;

      if (!isAllowedAudioUpload(file)) {
        updateAudioUploadProgress(currentIndex, -1);
        return null;
      }

      if (cancelledAudioUploadsRef.current.has(file)) {
        cancelledAudioUploadsRef.current.delete(file);
        return null;
      }

      try {
        const durationMs = await getAudioDuration(file);

        if (cancelledAudioUploadsRef.current.has(file)) {
          cancelledAudioUploadsRef.current.delete(file);
          return null;
        }

        return {
          ...audioItem,
          title: audioItem.title,
          artists: audioItem.artists || [],
          genre: audioItem.genre || '',
          order: currentIndex,
          durationMs: durationMs,
          coverImage: audioItem.coverImage || null,
          isUploading: false,
          uploadProgress: 100
        };
      } catch (error) {
        if (cancelledAudioUploadsRef.current.has(file)) {
          cancelledAudioUploadsRef.current.delete(file);
          return null;
        }

        updateAudioUploadProgress(currentIndex, -1);
        return null;
      }
    }));
    
    // Update audios with processed results
    setFormValues(prev => {
      const newAudios = [...(prev.audios || [])];
      
      processedAudios.forEach((processedAudio, idx) => {
        if (processedAudio === null) return;
        
        const originalItem = audioItems[idx];
        const itemIndex = newAudios.findIndex(a => a.file === originalItem.file);
        
        if (itemIndex >= 0) {
          if (!cancelledAudioUploadsRef.current.has(originalItem.file)) {
            newAudios[itemIndex] = {
              ...newAudios[itemIndex],
              ...processedAudio,
              isUploading: false,
              uploadProgress: 100
            };
          }
        }
      });
      
      // Remove cancelled items
      const finalAudios = newAudios.filter((audio, index) => {
        if (audio.isUploading && audio.file && cancelledAudioUploadsRef.current.has(audio.file)) {
          cancelledAudioUploadsRef.current.delete(audio.file);
          return false;
        }
        return true;
      });
      
      return {
        ...prev,
        audios: finalAudios
      };
    });
  };
  
  // Update audio upload progress
  const updateAudioUploadProgress = (itemIndex, progress) => {
    setFormValues(prev => {
      const newAudios = [...(prev.audios || [])];
      if (newAudios[itemIndex]) {
        newAudios[itemIndex] = {
          ...newAudios[itemIndex],
          uploadProgress: progress
        };
      }
      return {
        ...prev,
        audios: newAudios
      };
    });
  };
  
  const handleRemoveImage = (index) => {
    const item = formValues.images[index];
    
    // If item is uploading, cancel the upload
    if (item?.isUploading) {
      
      // Mark as cancelled (using item file as unique identifier)
      if (item.file) {
        cancelledUploadsRef.current.add(item.file);
      }
      
      // Clear progress interval
      if (uploadIntervalsRef.current[index]) {
        clearInterval(uploadIntervalsRef.current[index]);
        delete uploadIntervalsRef.current[index];
      }
      
      // Clean up preview URL if it exists
      if (item.previewUrl && item.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.previewUrl);
      }
      if (item.thumbnailUrl && item.thumbnailUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.thumbnailUrl);
      }
    }
    
    setFormValues(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      // Update order for remaining images
      const reorderedImages = newImages.map((img, idx) => ({
        ...img,
        order: idx
      }));
      return {
        ...prev,
        images: reorderedImages
      };
    });
    
    // Clear error if items exist now
    if (formValues.images.length > 1) {
      setErrors(prev => ({ ...prev, images: '' }));
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setFormValues(prev => {
      const images = [...prev.images];
      const draggedItem = images[draggedIndex];
      
      // Remove dragged item
      images.splice(draggedIndex, 1);
      
      // Insert at new position
      images.splice(dropIndex, 0, draggedItem);
      
      // Update order for all items
      const reorderedImages = images.map((img, idx) => ({
        ...img,
        order: idx
      }));
      
      return {
        ...prev,
        images: reorderedImages
      };
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  
  const handleRemoveAudio = (index) => {
    const audio = formValues.audios[index];
    
    // If audio is uploading, cancel the upload
    if (audio?.isUploading && audio?.file) {
      cancelledAudioUploadsRef.current.add(audio.file);
      
      // Clear progress interval
      if (audioUploadIntervalsRef.current[index]) {
        clearInterval(audioUploadIntervalsRef.current[index]);
        delete audioUploadIntervalsRef.current[index];
      }
    }
    
    setFormValues(prev => ({
      ...prev,
      audios: prev.audios.filter((_, i) => i !== index).map((audio, idx) => ({
        ...audio,
        order: idx
      }))
    }));
  };
  
  // Cover image handlers (similar to profile picture but square)
  const handleCoverImageClick = (audioIndex) => {
    setEditingCoverImageIndex(audioIndex);
    coverImageFileInputRef.current?.click();
  };
  
  const handleCoverImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!isAllowedProjectAudioCoverFile(file)) {
        notifyValidationToast("Nur JPG, PNG und WEBP sind erlaubt", "Ungültiges Format");
        if (coverImageFileInputRef.current) {
          coverImageFileInputRef.current.value = '';
        }
        return;
      }
      
      setCoverImageFormData({
        coverImage: file
      });
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result);
        setCoverImageModalOpen(true);
      };
      reader.onerror = () => {
        notifyValidationToast("Fehler beim Lesen der Datei");
        if (coverImageFileInputRef.current) {
          coverImageFileInputRef.current.value = '';
        }
      };
      reader.readAsDataURL(file);
    }
  };
  
  const closeCoverImageModal = () => {
    setCoverImageModalOpen(false);
    setCoverImagePreview(null);
    setCoverImageFormData({ coverImage: null });
    setCoverImageScale(1);
    setCoverImagePosition({ x: 0.5, y: 0.5 });
    setEditingCoverImageIndex(null);
    if (coverImageFileInputRef.current) {
      coverImageFileInputRef.current.value = '';
    }
  };
  
  const handleSaveCoverImage = () => {
    if (!coverImageFormData.coverImage || editingCoverImageIndex === null) return;
    
    // Validate file type
    if (!isAllowedProjectAudioCoverFile(coverImageFormData.coverImage)) {
      notifyValidationToast("Nur JPG, PNG und WEBP sind erlaubt", "Ungültiges Format");
      return;
    }
    
    if (coverImageEditorRef.current) {
      const canvas = coverImageEditorRef.current.getImageScaledToCanvas();
      
      try {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
        
        canvas.toBlob((blob) => {
          if (!blob) {
            return;
          }
          const editedFile = new File(
            [blob],
            (coverImageFormData.coverImage?.name?.split('.').slice(0, -1).join('.') || 'cover') + '.jpg',
            { type: 'image/jpeg' }
          );
          
          const coverImageData = {
            file: editedFile,
            url: dataUrl,
            fileName: editedFile.name,
            type: editedFile.type
          };
          
          // Update the audio item with cover image
          setFormValues(prev => {
            const newAudios = [...prev.audios];
            if (newAudios[editingCoverImageIndex]) {
              newAudios[editingCoverImageIndex] = {
                ...newAudios[editingCoverImageIndex],
                coverImage: coverImageData
              };
            }
            
            // Clear cover image error if all audios now have cover images
            const allHaveCoverImages = newAudios.every(audio => {
              const hasCoverImage = 
                (audio.coverImage && (typeof audio.coverImage === 'string' || audio.coverImage.file || audio.coverImage.url)) ||
                audio.processedCoverImage;
              return hasCoverImage;
            });
            
            if (allHaveCoverImages && errors.coverImages) {
              setTimeout(() => {
                setErrors(prev => ({ ...prev, coverImages: '' }));
              }, 0);
            }
            
            return {
              ...prev,
              audios: newAudios
            };
          });
          
          closeCoverImageModal();
        }, 'image/jpeg', 0.92);
      } catch (e) {
        notifySaveError(e, {
          title: 'Titelbild konnte nicht gespeichert werden',
        });
      }
      return;
    }
    
    // Fallback: if no editor, use original file
    const reader = new FileReader();
    reader.onloadend = () => {
      const coverImageData = {
        file: coverImageFormData.coverImage,
        url: reader.result,
        fileName: coverImageFormData.coverImage.name,
        type: coverImageFormData.coverImage.type
      };
      
      setFormValues(prev => {
        const newAudios = [...prev.audios];
        if (newAudios[editingCoverImageIndex]) {
          newAudios[editingCoverImageIndex] = {
            ...newAudios[editingCoverImageIndex],
            coverImage: coverImageData
          };
        }
        
        // Clear cover image error if all audios now have cover images
        const allHaveCoverImages = newAudios.every(audio => {
          const hasCoverImage = 
            (audio.coverImage && (typeof audio.coverImage === 'string' || audio.coverImage.file || audio.coverImage.url)) ||
            audio.processedCoverImage;
          return hasCoverImage;
        });
        
        if (allHaveCoverImages && errors.coverImages) {
          setTimeout(() => {
            setErrors(prev => ({ ...prev, coverImages: '' }));
          }, 0);
        }
        
        return {
          ...prev,
          audios: newAudios
        };
      });
      
      closeCoverImageModal();
    };
    reader.readAsDataURL(coverImageFormData.coverImage);
  };
  
  // Gesture handlers for cover image editor (similar to profile picture)
  const EDITOR_SIZE = 200;
  const PAN_DAMPING = 1.6;
  const PINCH_EXPONENT = 0.65;
  const WHEEL_STEP_IN = 1.02;
  const WHEEL_STEP_OUT = 0.98;
  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
  const clampPosition = (pos) => ({ x: clamp(pos.x, 0, 1), y: clamp(pos.y, 0, 1) });
  
  const coverImageGestureStateRef = useRef({
    isPanning: false,
    lastPoint: null,
    pinchStartDistance: null,
    startScale: 1,
    startPosition: { x: 0.5, y: 0.5 }
  });
  
  const handleCoverImageWheel = (e) => {
    const delta = -e.deltaY;
    const factor = delta > 0 ? WHEEL_STEP_IN : WHEEL_STEP_OUT;
    setCoverImageScale(prev => clamp(prev * factor, 1, 3));
  };
  
  const distanceBetween = (t1, t2) => {
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  };
  
  const midpoint = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2
  });
  
  const handleCoverImageTouchStart = (e) => {
    const state = coverImageGestureStateRef.current;
    const container = coverImageEditorContainerRef.current;
    if (!container) return;
    
    if (e.touches.length === 1) {
      state.isPanning = true;
      state.lastPoint = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      state.startPosition = coverImagePosition;
    } else if (e.touches.length === 2) {
      state.isPanning = true;
      state.pinchStartDistance = distanceBetween(e.touches[0], e.touches[1]);
      state.startScale = coverImageScale;
      state.startPosition = coverImagePosition;
      state.lastPoint = midpoint(e.touches[0], e.touches[1]);
    }
  };
  
  const handleCoverImageTouchMove = (e) => {
    const state = coverImageGestureStateRef.current;
    const container = coverImageEditorContainerRef.current;
    if (!container) return;
    
    if (e.touches.length === 1 && state.isPanning && state.lastPoint) {
      const current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      const dx = current.x - state.lastPoint.x;
      const dy = current.y - state.lastPoint.y;
      
      setCoverImagePosition(prev => clampPosition({
        x: prev.x - dx / (EDITOR_SIZE * PAN_DAMPING),
        y: prev.y - dy / (EDITOR_SIZE * PAN_DAMPING)
      }));
      
      state.lastPoint = current;
    }
    
    if (e.touches.length === 2) {
      const newDist = distanceBetween(e.touches[0], e.touches[1]);
      if (state.pinchStartDistance) {
        const ratio = newDist / state.pinchStartDistance;
        const softened = Math.pow(ratio, PINCH_EXPONENT);
        setCoverImageScale(clamp(state.startScale * softened, 1, 3));
      }
      const mid = midpoint(e.touches[0], e.touches[1]);
      const dx = mid.x - state.lastPoint.x;
      const dy = mid.y - state.lastPoint.y;
      setCoverImagePosition(prev => clampPosition({
        x: prev.x - dx / (EDITOR_SIZE * PAN_DAMPING),
        y: prev.y - dy / (EDITOR_SIZE * PAN_DAMPING)
      }));
      state.lastPoint = mid;
    }
  };
  
  const handleCoverImageTouchEnd = () => {
    coverImageGestureStateRef.current = {
      isPanning: false,
      lastPoint: null,
      pinchStartDistance: null,
      startScale: 1,
      startPosition: coverImagePosition
    };
  };
  
  // Handle replace audio
  const handleReplaceAudio = (index) => {
    // Trigger file input for audio
    const audioInput = document.getElementById('audio-upload');
    if (audioInput) {
      // Store the index to replace
      audioInput.dataset.replaceIndex = index;
      audioInput.click();
    }
  };
  
  // Validation function
  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      title: '',
      description: '',
      studioID: '',
      images: '',
      audios: '',
      coverImages: ''
    };
    
    // Validate title
    if (!formValues.title?.trim()) {
      newErrors.title = "Du musst einen Titel eingeben.";
      isValid = false;
    }
    
    // Validate description
    if (!formValues.description?.trim()) {
      newErrors.description = "Du musst eine Beschreibung für dein Projekt eingeben.";
      isValid = false;
    } else if (formValues.description?.trim().length < 10) {
      newErrors.description = "Du musst mindestens 10 Zeichen für deine Beschreibung eingeben.";
      isValid = false;
    }
    
    // Validate images for VISUAL type
    if (isVisualType && (!formValues.images || formValues.images.length === 0)) {
      newErrors.images = "Du musst mindestens ein Bild hinzufügen.";
      isValid = false;
    }
    
    // Check total size limit (100MB)
    if (isVisualType && formValues.images && formValues.images.length > 0) {
      const totalSize = calculateTotalSize(formValues.images);
      const maxSize = 100 * 1024 * 1024;
      if (totalSize > maxSize) {
        newErrors.images = `Die Gesamtgröße darf 100MB nicht überschreiten. Aktuell: ${(totalSize / (1024 * 1024)).toFixed(2)}MB`;
        isValid = false;
      }
    }
    
    // Validate audios for MUSIC type
    if (isMusicType && (!formValues.audios || formValues.audios.length === 0)) {
      newErrors.audios = "Du musst mindestens eine Audiodatei hinzufügen.";
      isValid = false;
    }
    
    // Validate cover images for MUSIC type - all audios must have cover images
    if (isMusicType && formValues.audios && formValues.audios.length > 0) {
      const audiosWithoutCoverImages = formValues.audios.filter(audio => {
        // Check if audio has a cover image (either as file, string, or processed)
        const hasCoverImage = 
          (audio.coverImage && (typeof audio.coverImage === 'string' || audio.coverImage.file || audio.coverImage.url)) ||
          audio.processedCoverImage;
        return !hasCoverImage;
      });
      
      if (audiosWithoutCoverImages.length > 0) {
        newErrors.coverImages = "Alle Audiodateien müssen ein Cover-Bild haben.";
        isValid = false;
      }
    }
    
    setErrors(newErrors);
    return isValid;
  };
  
  // Handle delete button click
  const handleDelete = async () => {
    if (!project?.id) return;

    const confirmed = window.confirm('Möchtest du dieses Projekt wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.');
    if (!confirmed) return;

    try {
      await handleDeleteProject({ id: project.id });
      // Navigate back to overview
      navigate(`/creative-editing/${creative.id}`);
    } catch (error) {
      notifyDeleteError(error, { onRetry: () => handleDelete() });
    }
  };

  // Handle save button click
  const handleSave = async () => {
    // Validate form first
    if (!validateForm()) {
      return;
    }
    
    // For editing, check if there are changes
    if (isEditing && !hasChanges()) {
      return;
    }
    
    try {
      // Map display status (from toggle) to GraphQL enum value:
      // ACTIVE (toggle on) → PUBLISHED (active project)
      // INACTIVE (toggle off) → INACTIVE (inactive project)
      // Note: Projects use 'status' field (PUBLISHED = active, INACTIVE/DRAFT = inactive)
      // Backend automatically updates creative.canBePublished based on active projects/packages
      // For new projects, default to PUBLISHED (ACTIVE toggle)
      const displayStatus = status || 'ACTIVE';
      const projectStatus = displayStatus === 'ACTIVE' ? 'PUBLISHED' : 'INACTIVE';

      if (isMusicType) {
        // Validate that all audios have cover images before processing
        const audiosWithoutCoverImages = formValues.audios.filter(audio => {
          const hasCoverImage = 
            (audio.coverImage && (typeof audio.coverImage === 'string' || audio.coverImage.file || audio.coverImage.url)) ||
            audio.processedCoverImage;
          return !hasCoverImage;
        });
        
        if (audiosWithoutCoverImages.length > 0) {
          setErrors(prev => ({ 
            ...prev, 
            coverImages: "Alle Audiodateien müssen ein Cover-Bild haben. Bitte füge für jede Audiodatei ein Cover-Bild hinzu." 
          }));
          // Scroll to audio section
          setTimeout(() => {
            const audioSection = document.querySelector(`.${classes.mediaContainer}`);
            if (audioSection) {
              audioSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
          return;
        }
      }

      const buildProjectData = ({ projectId: resolvedProjectId, images, audios }) => {
        const projectData = {
          id: resolvedProjectId,
          creativeID: creative.id,
          title: formValues.title.trim(),
          description: formValues.description.trim(),
          status: projectStatus, // PUBLISHED = active, INACTIVE = inactive
          studioID: formValues.studioID
        };

        if (isVisualType) {
          projectData.images = images;
        }

        if (isMusicType) {
          projectData.audios = audios;
        }

        return projectData;
      };

      await saveCreativeProjectWithOwnedMedia({
        creative,
        isEditing,
        projectId,
        project,
        images: isVisualType ? formValues.images : [],
        audios: isMusicType ? formValues.audios : [],
        formArtists: formValues.artists,
        formGenre: formValues.genre,
        buildProjectData,
        handleCreateProject,
        handleUpdateProject,
      });

      // Navigate back to overview with saved state
      navigate(`/creative-editing/${creative.id}/overview`, { state: { saved: true } });
    } catch (error) {
      notifySaveError(error, { onRetry: () => handleSave() });
      
      // Extract error message
      let errorMessage = 'Fehler beim Speichern: ';
      if (error.message) {
        errorMessage += error.message;
      } else if (error.errors && error.errors.length > 0) {
        errorMessage += error.errors.map(e => e.message || e).join(', ');
      } else {
        errorMessage += 'Unbekannter Fehler';
      }
      
      // Set error in state based on error type
      if (isMusicType && errorMessage.toLowerCase().includes('cover')) {
        setErrors(prev => ({ ...prev, coverImages: errorMessage }));
      } else if (isMusicType) {
        setErrors(prev => ({ ...prev, audios: errorMessage }));
      } else if (isVisualType) {
        setErrors(prev => ({ ...prev, images: errorMessage }));
      } else {
        // General error - show in description field
        setErrors(prev => ({ ...prev, description: errorMessage }));
      }
      
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    navigate(-1);
  };
  
  // Fetch selected studio using the new hook
  const { studio: fetchedStudio, loading: studioLoading } = useFetchStudioById(formValues.studioID);
  
  // Get selected studio for display (fallback to creative's studios if hook hasn't loaded yet)
  const selectedStudioItem = creative?.studios?.items?.find(
    item => item.studioID === formValues.studioID
  );
  const selectedStudio = fetchedStudio || selectedStudioItem?.studio;
  
  // Show loading if creative or project is loading
  if (loading || (projectId && projectLoading)) {
    return (
      <div className={classes.creativeProfileEditPage}>
        <div className={classes.contentContainer}>
          <header className={classes.skeletonHeader}>
            <div className={classes.skeletonBackButton}>
              <div className={classes.skeletonBackIcon}></div>
              <div className={classes.skeletonBackText}></div>
            </div>
            <div className={classes.skeletonSaveButton}></div>
          </header>
          <div className={classes.mainContent}>
            <div className={classes.textContainer}>
              <div className={classes.skeletonTitle}></div>
              <div className={classes.skeletonDescription} style={{ width: '70%', height: '60px', marginTop: '8px' }}></div>
            </div>
            <div className={classes.inputsContainer}>
              <div className={classes.titleInputContainer}>
                <div className={classes.skeletonInput} style={{ height: '24px', width: '80px', marginBottom: '12px' }}></div>
                <div className={classes.skeletonInput}></div>
              </div>
              <div className={classes.descriptionInputContainer}>
                <div className={classes.skeletonInput} style={{ height: '24px', width: '120px', marginBottom: '12px' }}></div>
                <div className={classes.skeletonDescription}></div>
              </div>
              {isVisualType ? (
                <div className={classes.fotoMediaContainer}>
                  <div className={classes.skeletonInput} style={{ height: '24px', width: '140px', marginBottom: '12px' }}></div>
                  <div className={classes.skeletonMediaGrid}>
                    {[...Array(4)].map((_, index) => (
                      <div key={index} className={classes.skeletonMediaItem}></div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={classes.mediaContainer}>
                  <div className={classes.skeletonAudioItem}>
                    <div className={classes.skeletonCoverImage}></div>
                    <div className={classes.skeletonAudioInfo}>
                      <div className={classes.skeletonAudioText}></div>
                      <div className={classes.skeletonAudioTitle}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if creative or project has error
  if (error || (projectId && projectError)) {
    return (
      <div className={classes.contentContainer}>
        <div className={classes.errorContainer}>
          <p>Creative-Projekte konnten nicht geladen werden.</p>
        </div>
      </div>
    );
  }

  if (!creative) {
    return (
      <div className={classes.contentContainer}>
        <div className={classes.errorContainer}>
          <p>Creative-Profil wurde nicht gefunden.</p>
        </div>
      </div>
    );
  }

  // If editing but project not found
  if (projectId && !project) {
    return (
      <div className={classes.contentContainer}>
        <div className={classes.errorContainer}>
          <p>Projekt wurde nicht gefunden.</p>
        </div>
      </div>
    );
  }

  const isLoading = createLoading || updateLoading;

  return (
    <div className={classes.creativeProfileEditPage}>
        <div className={classes.contentContainer}>
          <header className={classes.headerContainer}>
            <div className={classes.header}>
              <Link to={`/creative-editing/${creative?.id}`} className={classes.backButtonPhone}>
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>
              </Link>
              <button onClick={handleBack} className={classes.backButton}>
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                  <path d="M6.33301 0.0712891L7.15039 0.888672L7.22168 0.958984L1.88281 6.29688L7.18652 12.041L7.25391 12.1143L7.18066 12.1826L6.33105 12.9668L6.25781 13.0342L6.18945 12.9609L0.0771484 6.33984L0.078125 6.33887L0.0712891 6.33301L0 6.2627L6.2627 0L6.33301 0.0712891Z" fill="#1F1F1F"/>
                </svg>
                <p className="semiboldSmall">Zurück</p>
              </button>
              <button 
                className={`${classes.saveButton} ${canSave() ? classes.saveButtonActive : ''} mediumLarge`}
                onClick={handleSave}
                disabled={!canSave() || isLoading}
              >
                {isLoading ? 'Speichern...' : (isEditing ? 'Änderungen speichern' : 'Projekt hinzufügen')}
              </button>
            </div>
          </header>
          {isVisualType ? (
          <div className={classes.mainContent}>
            <div className={classes.textContainer}>
              <h3 className="titleSemibold">{isEditing ? 'Bearbeite dein Projekt' : 'Füge ein Projekt hinzu.'}</h3>
              <p className="slimRegularLargeText" style={{color: "var(--dark-grey-4)"}}>
                {isEditing 
                  ? 'Bearbeite dein Projekt und aktualisiere die Informationen.'
                  : 'Füge ein Projekt hinzu, das zeigt, was du kannst. Shootings oder Videodreh.'
                }
              </p>
            </div>
            <div className={classes.inputsContainer}>
              
              {isEditing && (
                <div className={classes.activateContainer}>
                  <header className={classes.activateHeader}>
                    <div className={classes.activateTitle}>
                      <h4 className="semiboldMiddle">Projekt Status</h4>
                    </div>
                    <div className={classes.switchContainer}>
                        <label className={classes.switch}>
                            <input type="checkbox" checked={status === 'ACTIVE'} onChange={handleStatusChange} />
                            <span className={classes.slider}></span>
                        </label>
                    </div>
                  </header>
                </div>
              )}

              {/* Title Input */}
              <div className={classes.titleInputContainer}>
                <label className="semiboldMiddle">Titel</label>
                <input 
                    type="text" 
                    className={`${classes.addUserInfosInputText} regularMiddle ${errors.title ? classes.inputError : ''}`} 
                    placeholder="Titel eingeben" 
                    value={formValues.title || ''} 
                    onChange={handleTitleChange}
                    maxLength={100}
                />
                <div className={classes.inputFooter}>
                    {errors.title && (
                        <ErrorMessage>{errors.title}</ErrorMessage>
                    )}
                    <div className={`${classes.textCounter} slimRegularSmallText`}>
                        {(formValues.title?.length || 0)} / 100
                    </div>
                </div>
              </div>
              
              {/* Description Input */}
              <div className={classes.descriptionInputContainer}>
                <label className="semiboldMiddle">Beschreibung</label>
                <textarea 
                    className={`${classes.descriptionTextarea} regularMiddle ${errors.description ? classes.inputError : ''}`} 
                    placeholder="Beschreibe dein Projekt..."
                    value={formValues.description || ''} 
                    onChange={handleDescriptionChange}
                    maxLength={500}
                />
                <div className={classes.inputFooter}>
                    {errors.description && (
                        <ErrorMessage>{errors.description}</ErrorMessage>
                    )}
                    <div className={`${classes.textCounter} slimRegularSmallText`}>
                        {(formValues.description?.length || 0)} / 500
                    </div>
                </div>
              </div>
              
              {/* Studio Selection */}
              <div className={classes.studioWrapper}>
                {/* <h3 className="semiboldMiddle">Studio</h3> */}
                {isStudioCheckboxChecked && formValues?.studioID && selectedStudio ? (
                  <div className={classes.studioSelectedContainer}>
                    <div className={classes.studioInputContainer} onClick={handleStudioCheckboxClick}>
                      <input
                        type="checkbox"
                        className={classes.studioCheckbox}
                        checked={true}
                        readOnly
                        onClick={handleStudioCheckboxClick}
                      />
                      <span className="regularMiddle">Projekt wurde in meinem Studio realisiert.</span>
                    </div>
                    <div className={classes.studioSelectedContentWrapper}>
                      <div className={classes.studioSelectedContent}>
                        <div className={classes.studioImageContainer}>
                          {selectedStudio.image ? (
                            <img src={selectedStudio.image} alt={selectedStudio.StudioName} />
                          ) : selectedStudio?.processedImages?.[0] ? (
                            <img src={selectedStudio.processedImages[0]} alt={selectedStudio.StudioName} />
                          ) : null}
                        </div>
                         <div className={classes.studioSelectedInfo}>
                           <span className="regularMiddle">{selectedStudio.StudioName}</span>
                           {buildCoarseLocationText(selectedStudio) && (
                             <span className={`${classes.studioOwnerName} slimRegularSmallText`}>
                               {buildCoarseLocationText(selectedStudio)}
                             </span>
                           )}
                         </div>
                      </div>
                      <button 
                        className={`${classes.changeStudioButton} regularSmall`}
                        onClick={() => setShowChangeStudioModal(true)}
                        type="button"
                      >
                        Studio ändern
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={classes.studioInputContainer} onClick={handleStudioCheckboxClick}>
                    <input
                      type="checkbox"
                      className={classes.studioCheckbox}
                      checked={false}
                      readOnly
                    />
                    <span className="regularMiddle">Projekt wurde in meinem Studio realisiert.</span>
                  </div>
                )}
                {errors.studioID && (
                  <ErrorMessage>{errors.studioID}</ErrorMessage>
                )}
              </div>
              
              {/* Images for VISUAL type */}
              {isVisualType && (
                <div className={classes.fotoMediaContainer}>
                  <label className="semiboldMiddle">Fotos & Videos</label>
                  <div className={classes.videoFotoInfoBox}>
                    <span>💡</span>
                    <p>Fotos & Videos bis 100MB.<br />
                    Größere Videos per YouTube oder Vimeo.</p>
                  </div>
                  <button 
                    className={classes.uploadButton}
                    onClick={() => setShowAddMediaModal(true)}
                    type="button"
                  >
                    <span className="mediumLarge">Fotos, Videos oder Link hinzufügen</span>
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept={PROJECT_IMAGE_ACCEPT}
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e, 'photo')}
                  />
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept={PROJECT_VIDEO_ACCEPT}
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e, 'video')}
                  />
                  {errors.images && (
                    <ErrorMessage>{errors.images}</ErrorMessage>
                  )}
                  {formValues.images.length > 0 && (
                    <div className={classes.mediaPreview}>
                      {formValues.images.map((item, index) => {
                        // Handle different media types
                        let displayUrl = '';
                        const urlToCheck = item.url || '';
                        const isLink = item.type === 'link' || 
                          (urlToCheck && typeof urlToCheck === 'string' && 
                           (urlToCheck.includes('youtube.com') || urlToCheck.includes('youtu.be') || urlToCheck.includes('vimeo.com')));
                        
                        // Check if it's a supported project video.
                        const isVideo = item.type === 'video' || 
                          (urlToCheck && typeof urlToCheck === 'string' && 
                           /\.(mp4|mov|webm)$/i.test(urlToCheck));
                        const isPhoto = item.type === 'photo' || (!item.type && !isLink && !isVideo);
                        
                        if (isLink && item.thumbnailUrl) {
                          displayUrl = item.thumbnailUrl;
                        } else if (isVideo) {
                          // Video - handle thumbnail URL similar to ProjectOverview
                          // Prioritize processedThumbnailUrl if available (from processedImages)
                          const thumbnailToUse = item.processedThumbnailUrl || item.thumbnailUrl;
                          
                          if (thumbnailToUse) {
                            // Check if thumbnailUrl is a blob URL or full HTTP URL
                            if (thumbnailToUse.startsWith('blob:') || thumbnailToUse.startsWith('http')) {
                              displayUrl = thumbnailToUse;
                            } else {
                              // It's an S3 key - process through CloudFront
                              try {
                                displayUrl = getCloudFrontImageUrl(thumbnailToUse, {
                                  width: 300,
                                  height: 300,
                                  fit: 'cover'
                                });
                              } catch (error) {
                                // Fallback to S3 URL
                                try {
                                  const cleanKey = thumbnailToUse.startsWith('public/') ? thumbnailToUse : `public/${thumbnailToUse}`;
                                  displayUrl = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                                } catch (fallbackError) {
                                  displayUrl = thumbnailToUse;
                                }
                              }
                            }
                          } else if (item.previewUrl) {
                            // Newly added video with preview thumbnail
                            displayUrl = item.previewUrl;
                          } else {
                            // No thumbnail available
                            displayUrl = '';
                          }
                        } else if (item.previewUrl) {
                          // Newly added file with preview
                          displayUrl = item.previewUrl;
                        } else if (item.url) {
                          // Existing uploaded file (photo)
                          const imageUrl = typeof item === 'string' ? item : (item.url || item.processedUrl);
                          if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
                            try {
                              displayUrl = getCloudFrontImageUrl(imageUrl, {
                                width: 300,
                                height: 300,
                                fit: 'cover'
                              });
                            } catch (error) {
                              try {
                                const cleanKey = imageUrl.startsWith('public/') ? imageUrl : `public/${imageUrl}`;
                                displayUrl = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                              } catch (fallbackError) {
                                displayUrl = imageUrl;
                              }
                            }
                          } else {
                            displayUrl = imageUrl;
                          }
                        }
                        
                        const isUploading = item.isUploading === true;
                        const uploadProgress = item.uploadProgress || 0;
                        const hasError = uploadProgress === -1;
                        
                        return (
                          <div 
                            key={index} 
                            className={`${classes.mediaItem} ${draggedIndex === index ? classes.dragging : ''} ${dragOverIndex === index ? classes.dragOver : ''} ${isUploading ? classes.uploading : ''}`}
                            draggable={!isUploading}
                            onDragStart={(e) => !isUploading && handleDragStart(e, index)}
                            onDragOver={(e) => !isUploading && handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => !isUploading && handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                            {/* {isLink && (
                              <div className={classes.linkBadge}>
                                <span>🔗</span>
                              </div>
                            )}
                            {isVideo && !isLink && (
                              <div className={classes.videoBadge}>
                                <span>🎥</span>
                              </div>
                            )} */}
                            {displayUrl ? (
                              <img src={displayUrl} alt={`Preview ${index + 1}`} />
                            // ) : isVideo ? (
                            //   <div className={classes.videoPlaceholder}>
                            //     <span>🎥</span>
                            //     <p>Video</p>
                            //   </div>
                            ) : null}
                            {isUploading && (
                              <div className={classes.uploadProgressOverlay}>
                                <div className={classes.circularProgress}>
                                  <svg className={classes.progressSvg} viewBox="0 0 36 36">
                                    <circle
                                      className={classes.progressCircleBg}
                                      cx="18"
                                      cy="18"
                                      r="16"
                                      fill="none"
                                    />
                                    <circle
                                      className={classes.progressCircle}
                                      cx="18"
                                      cy="18"
                                      r="16"
                                      fill="none"
                                      strokeDasharray={`${uploadProgress * 100.53 / 100}, 100.53`}
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                            {hasError && (
                              <div className={classes.uploadErrorOverlay}>
                                <span>⚠️</span>
                              </div>
                            )}
                            <button 
                              className={classes.removeButton}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveImage(index);
                              }}
                              type="button"
                              title={isUploading ? "Upload abbrechen" : "Entfernen"}
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {isEditing && (
                <div className={classes.deleteButtonContainer}>
                  <button 
                    className={classes.deleteButton}
                    onClick={handleDelete}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? 'Löschen...' : 'Projekt löschen'}
                  </button>
                </div>
              )}
            </div>
          </div>
          ) : (
            <div className={classes.mainContent}>
              <div className={`${classes.mediaContainer} ${formValues?.audios?.length > 0 ? classes.mediaContainerActive : ''}`}>
                {/* Always render the audio input, but keep it hidden */}
                  <input
                    type="file"
                    accept={PROJECT_AUDIO_ACCEPT}
                    onChange={handleAudioUpload}
                    style={{ display: 'none' }}
                    id="audio-upload"
                />
                {formValues?.audios?.length === 0 ? (
                  <label htmlFor="audio-upload" className={classes.uploadButtonMedia}>
                    Audio-Datei hochladen
                  </label>
                ) : (
                  <div className={classes.audioUploadContainer}>
                    {formValues.audios.map((audio, index) => {
                      const isUploading = audio.isUploading === true;
                      const uploadProgress = audio.uploadProgress || 0;
                      const hasError = uploadProgress === -1;
                      
                      // Get cover image URL - could be from new upload (url property), processed (processedCoverImage), or existing (coverImage string)
                      let displayCoverImage = null;
                      if (audio.coverImage) {
                        // First check if we have a processed cover image (from hook)
                        if (audio.processedCoverImage) {
                          displayCoverImage = audio.processedCoverImage;
                        } else if (typeof audio.coverImage === 'string') {
                          // Existing cover image from database - process it
                          try {
                            displayCoverImage = getCloudFrontImageUrl(audio.coverImage, {
                              width: 300,
                              height: 300,
                              fit: 'cover'
                            });
                          } catch (error) {
                            try {
                              const cleanKey = audio.coverImage.startsWith('public/') ? audio.coverImage : `public/${audio.coverImage}`;
                              displayCoverImage = `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
                            } catch (fallbackError) {
                              displayCoverImage = audio.coverImage;
                            }
                          }
                        } else if (audio.coverImage.url) {
                          // New cover image with preview URL
                          displayCoverImage = audio.coverImage.url;
                        }
                      }
                      
                      // Check if this audio is missing a cover image
                      const hasCoverImage = 
                        (audio.coverImage && (typeof audio.coverImage === 'string' || audio.coverImage.file || audio.coverImage.url)) ||
                        audio.processedCoverImage;
                      const isMissingCoverImage = !hasCoverImage && !isUploading;
                      
                      return (
                        <div key={index} className={classes.audioUploadItem}>
                          {/* Cover Image Box */}
                          <div 
                            className={`${classes.coverImageBox} ${displayCoverImage ? classes.coverImageBoxActive : ''} ${isMissingCoverImage && errors.coverImages ? classes.coverImageBoxError : ''}`}
                            onClick={() => !isUploading && handleCoverImageClick(index)}
                          >
                            {displayCoverImage ? (
                              <img src={displayCoverImage} alt="Cover" />
                            ) : (
                              <div className={classes.coverImagePlaceholder}>
                                <span style={{ color: isMissingCoverImage && errors.coverImages ? 'var(--error-color, #FF003E)' : 'var(--dark-grey-4)' }} className="regularMiddle">
                                  {isMissingCoverImage && errors.coverImages ? '⚠️ Cover-Bild erforderlich' : 'Cover-Bild hinzufügen'}
                                </span>
                              </div>
                            )}
                            {displayCoverImage && (
                              <button
                                className={`${classes.removeCoverImageButton} semiboldSmall`}
                                onClick={(e) => { 
                                  e.stopPropagation();
                                  handleCoverImageClick(index);
                                }}
                              >
                                Ersetzen
                              </button>
                            )}
                          </div>
                          
                          {/* Audio Info */}
                          <div className={classes.audioInfoContainer}>
                            <span style={{ color: 'var(--dark-grey-4)' }} className="regularMiddle">Dateiname</span>
                            <span
                              style={{ marginTop: '4px', marginBottom: '16px' }}
                              className={`${classes.audioTitleTruncate} semiboldMiddle`}
                            >
                              {audio.title || `Audio ${index + 1}`}
                            </span>
                            
                            {isUploading && (
                              <div className={classes.audioUploadProgress}>
                                <div className={classes.circularProgress}>
                                  <svg className={classes.progressSvg} viewBox="0 0 36 36">
                                    <circle
                                      className={classes.progressCircleBg}
                                      cx="18"
                                      cy="18"
                                      r="16"
                                      fill="none"
                                    />
                                    <circle
                                      className={classes.progressCircle}
                                      cx="18"
                                      cy="18"
                                      r="16"
                                      fill="none"
                                      strokeDasharray={`${(uploadProgress / 100) * 100.53}, 100.53`}
                                    />
                                  </svg>
                                  <button
                                    className={classes.cancelUploadButton}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveAudio(index);
                                    }}
                                    type="button"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            )}
                            
                            {!isUploading && !hasError && (
                              <button
                                className={`${classes.replaceAudioButton} semiboldSmall`}
                                onClick={() => handleReplaceAudio(index)}
                                type="button"
                              >
                                Ersetzen
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Hidden file input for cover images */}
                <input
                  ref={coverImageFileInputRef}
                  type="file"
                  accept={PROJECT_AUDIO_COVER_ACCEPT}
                  onChange={handleCoverImageFileChange}
                  style={{ display: 'none' }}
                />
                
                {errors.audios && (
                  <ErrorMessage>{errors.audios}</ErrorMessage>
                )}
                {errors.coverImages && formValues.audios && formValues.audios.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <ErrorMessage>{errors.coverImages}</ErrorMessage>
                  </div>
                )}
              </div>
              {/* Title Input */}
              <div className={classes.titleInputContainer}>
                <label className="semiboldMiddle">Titel</label>
                <input 
                    type="text" 
                    className={`${classes.addUserInfosInputText} regularMiddle ${errors.title ? classes.inputError : ''}`} 
                    placeholder="Titel eingeben" 
                    value={formValues.title || ''} 
                    onChange={handleTitleChange}
                    maxLength={100}
                />
                <div className={classes.inputFooter}>
                    {errors.title && (
                        <ErrorMessage>{errors.title}</ErrorMessage>
                    )}
                    <div className={`${classes.textCounter} slimRegularSmallText`}>
                        {(formValues.title?.length || 0)} / 100
                    </div>
                </div>
              </div>

               {/* Künstler  */}
               <div className={classes.artistContainer}>
                 <label className="semiboldMiddle">Künstler</label>
                 <div className={classes.artistNameInputContainer}>
                   {formValues.artists && formValues.artists.length > 0 && (
                     <div className={classes.artistNameWrapper}>
                       {formValues.artists.map((artist, index) => (
                         <div key={index} className={classes.artistNameContainer}>
                           <span className="regularMiddle">{artist}</span>
                           <svg 
                             onClick={() => handleRemoveArtist(index)} 
                             xmlns="http://www.w3.org/2000/svg" 
                             width="10" 
                             height="10" 
                             viewBox="0 0 10 10" 
                             fill="none"
                             style={{ cursor: 'pointer' }}
                           >
                             <path d="M10 0.704653L9.93067 0.773029L5.7037 5L9.93067 9.22697L10 9.29535L9.29535 10L9.22697 9.93067L5 5.7037L0.773029 9.93067L0.704653 10L0 9.29535L0.0693257 9.22697L4.2963 5L0.0693257 0.773029L0 0.704653L0.704653 0L0.773029 0.0693257L5 4.2963L9.22697 0.0693257L9.29535 0L10 0.704653Z" fill="#1F1F1F"/>
                           </svg>
                         </div>
                       ))}
                     </div>
                   )}
                   <input 
                     type="text" 
                     className={`${classes.addArtistInput} regularMiddle ${errors.artist ? classes.inputError : ''}`} 
                     placeholder="Mitwirkende hinzufügen (Enter drücken)" 
                     value={currentArtistInput} 
                     onChange={handleArtistInputChange}
                     onKeyDown={handleArtistKeyDown}
                     maxLength={100}
                   />
                 </div>
                 <div className={classes.inputFooter}>
                   {errors.artist && (
                     <ErrorMessage>{errors.artist}</ErrorMessage>
                   )}
                   <div className={`${classes.textCounter} slimRegularSmallText`}>
                     {(currentArtistInput?.length || 0)} / 100
                   </div>
                 </div>
               </div>

                {/* Studio Selection */}
                <div className={classes.studioWrapper}>
                  {/* <h3 className="semiboldMiddle">Studio</h3> */}
                  {isStudioCheckboxChecked && formValues?.studioID && selectedStudio ? (
                    <div className={classes.studioSelectedContainer}>
                      <div className={classes.studioInputContainer} onClick={handleStudioCheckboxClick}>
                        <input
                          type="checkbox"
                          className={classes.studioCheckbox}
                          checked={true}
                          readOnly
                          onClick={handleStudioCheckboxClick}
                        />
                        <span className="regularMiddle">Projekt wurde in meinem Studio realisiert.</span>
                      </div>
                      <div className={classes.studioSelectedContentWrapper}>
                        <div className={classes.studioSelectedContent}>
                          <div className={classes.studioImageContainer}>
                            {selectedStudio.image ? (
                              <img src={selectedStudio.image} alt={selectedStudio.StudioName} />
                            ) : selectedStudio?.processedImages?.[0] ? (
                              <img src={selectedStudio.processedImages[0]} alt={selectedStudio.StudioName} />
                            ) : null}
                          </div>
                         <div className={classes.studioSelectedInfo}>
                           <span className="regularMiddle">{selectedStudio.StudioName}</span>
                           {buildCoarseLocationText(selectedStudio) && (
                             <span className={`${classes.studioOwnerName} slimRegularSmallText`}>
                               {buildCoarseLocationText(selectedStudio)}
                             </span>
                           )}
                         </div>
                        </div>
                        <button 
                          className={`${classes.changeStudioButton} regularSmall`}
                          onClick={() => setShowChangeStudioModal(true)}
                          type="button"
                        >
                          Studio ändern
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={classes.studioInputContainer} onClick={handleStudioCheckboxClick}>
                      <input
                        type="checkbox"
                        className={classes.studioCheckbox}
                        checked={false}
                        readOnly
                      />
                      <span className="regularMiddle">Projekt wurde in meinem Studio realisiert.</span>
                    </div>
                  )}
                  {errors.studioID && (
                    <ErrorMessage>{errors.studioID}</ErrorMessage>
                  )}
                </div>

                {/* Genre Selection */}
                <div className={classes.genreWrapper}>
                  <label className="semiboldMiddle">Genre</label>
                  <div className={classes.genreDropdownContainer}>
                    <button
                      ref={genreButtonRef}
                      type="button"
                      className={classes.genreDropdownButton}
                      onClick={toggleGenreDropdown}
                    >
                      <span className={`regularMiddle ${!formValues.genre ? classes.genreDropdownButtonPlaceholder : ''}`}>
                        {formValues.genre || 'Genre wählen & gefunden werden.'}
                      </span>
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="18" 
                        height="10" 
                        viewBox="0 0 18 10" 
                        fill="none"
                        className={`${classes.genreDropdownArrow} ${isGenreDropdownOpen ? classes.genreDropdownArrowOpen : ''}`}
                      >
                        <path 
                          fillRule="evenodd" 
                          clipRule="evenodd" 
                          d="M17.5 2.1491L8.40159 9.44336L0 2.1491L1.10495 0.973254L8.45136 7.35075L16.4448 0.943359L17.5 2.1491Z" 
                          fill="#1F1F1F"
                        />
                      </svg>
                    </button>
                    {isGenreDropdownOpen && (
                      <div ref={genreDropdownRef} className={classes.genreDropdownList}>
                        <div className={classes.genreDropdownContent}>
                          {genreTags.map((genre, index) => (
                            <div
                              key={index}
                              className={`${classes.genreDropdownOption} ${formValues.genre === genre ? classes.genreDropdownOptionSelected : ''}`}
                              onClick={() => handleGenreSelect(genre)}
                            >
                              <span className="regularMiddle">{genre}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.genre && (
                    <ErrorMessage>{errors.genre}</ErrorMessage>
                  )}
                </div>

                {/* Description Input */}
                <div className={classes.descriptionInputContainer}>
                  <label className="semiboldMiddle">Beschreibung</label>
                  <textarea 
                      className={`${classes.descriptionTextarea} regularMiddle ${errors.description ? classes.inputError : ''}`} 
                      placeholder="Beschreibe dein Projekt..."
                      value={formValues.description || ''} 
                      onChange={handleDescriptionChange}
                      maxLength={500}
                  />
                  <div className={classes.inputFooter}>
                      {errors.description && (
                          <ErrorMessage>{errors.description}</ErrorMessage>
                      )}
                      <div className={`${classes.textCounter} slimRegularSmallText`}>
                          {(formValues.description?.length || 0)} / 500
                      </div>
                  </div>
                </div>

                {isEditing && (
                  <div className={classes.deleteButtonContainer}>
                    <button 
                      className={classes.deleteButton}
                      onClick={handleDelete}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? 'Löschen...' : 'Projekt löschen'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        
        {showChangeStudioModal && 
          <ChangeStudioModal 
            userID={creative?.userID} 
            creative={creative} 
            onClose={() => setShowChangeStudioModal(false)} 
            showChangeStudioModal={showChangeStudioModal}
            setShowChangeStudioModal={setShowChangeStudioModal}
            onStudioSelect={handleStudioSelect}
            selectedStudioId={formValues.studioID}
          />
        }
        
        {showAddMediaModal && (
          <AddingVideoFotoModal
            open={showAddMediaModal}
            onBackdropClick={() => setShowAddMediaModal(false)}
            onModeSelect={handleModeSelect}
            onLinkSubmit={handleLinkSubmit}
          />
        )}
        
        {/* Cover Image Editor Modal */}
        <SmallModal
          open={coverImageModalOpen}
          onBackdropClick={closeCoverImageModal}
          fromCreateService={true}
        >
          <div className={classes.modalContainer}>
            <header className={classes.modalHeader}>
              <h2 className='titleSemibold3'>Vorschau deines Bilds.</h2>
              <p className='regularXxs' style={{color: 'var(--dark-grey-4)'}}>
              Ideal: JPG/PNG, mind. 300×300 px.
              </p>
            </header>
            <div className={classes.modalContent}>
              {coverImagePreview && (
                <div className={classes.previewContainer}>
                  <div
                    className={classes.coverImagePreviewContainer}
                    ref={coverImageEditorContainerRef}
                    onWheel={handleCoverImageWheel}
                    onTouchStart={handleCoverImageTouchStart}
                    onTouchMove={handleCoverImageTouchMove}
                    onTouchEnd={handleCoverImageTouchEnd}
                    onTouchCancel={handleCoverImageTouchEnd}
                  >
                    <AvatarEditor
                      ref={coverImageEditorRef}
                      image={coverImagePreview}
                      width={200}
                      height={200}
                      border={0}
                      borderRadius={0}
                      color={[255, 255, 255, 0]}
                      scale={coverImageScale}
                      rotate={0}
                      position={coverImagePosition}
                      onPositionChange={setCoverImagePosition}
                      style={{ width: '200px', height: '200px', borderRadius: '0' }}
                    />
                  </div>
                </div>
              )}
            </div>
            <footer className={classes.modalFooter}>
              <button onClick={closeCoverImageModal} className={`${classes.cancelButton} mediumLarge`}>Abbrechen</button>
              <button 
                onClick={handleSaveCoverImage} 
                className={`${classes.saveButtonModal} mediumLarge`}
                disabled={!coverImageFormData.coverImage}
              >
                Speichern
              </button>
            </footer>
          </div>
        </SmallModal>
    </div>
  );
};

export default CreativeProjectEdit;
